import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getCurrentAuthUser } from "@/Service/authService";
import { getAnalytics } from "./analytics";

/**
 * Bind the authenticated user's identity into the analytics provider.
 *
 * Identity is composed from two sources because the codebase splits them:
 *   - Firebase Auth (getCurrentAuthUser)  → uid, email, displayName
 *   - Firestore user doc (state.auth.currentUser) → role and any extra fields
 *
 * The spec asked for { userId, email, companyId, companyName }. This codebase
 * doesn't model companies as a first-class concept yet — the closest equivalent
 * is `role` and whatever custom fields exist in the Firestore user doc. We
 * pass everything the Firestore doc contains (minus sensitive keys) so that
 * once product introduces org/company fields, they surface automatically
 * without editing this hook.
 *
 * The dependency on state.auth.currentUser drives:
 *   - identify() on sign-in (or Firestore doc refresh)
 *   - reset()    on sign-out (currentUser becomes null)
 *
 * The ref guard prevents duplicate identify() calls if the Firestore doc
 * updates without a meaningful identity change (e.g. background metadata
 * refresh).
 */
export const useObservabilityIdentity = () => {
  const currentUser = useSelector((state) => state.auth.currentUser);
  const lastIdentityKeyRef = useRef(null);

  useEffect(() => {
    const analytics = getAnalytics();
    const firebaseUser = getCurrentAuthUser();
    const userId = firebaseUser?.uid || currentUser?.uid || null;

    if (!userId) {
      // Only reset if we previously identified — avoids gratuitous reset() on
      // the initial anonymous render.
      if (lastIdentityKeyRef.current) {
        analytics.reset();
        lastIdentityKeyRef.current = null;
      }
      return;
    }

    // Merge Firebase auth (canonical email) with the Firestore doc (role +
    // whatever else product stores). Filter out anything that looks sensitive.
    const traits = buildTraits({ firebaseUser, userDoc: currentUser });
    const identityKey = `${userId}:${JSON.stringify(traits)}`;
    if (identityKey === lastIdentityKeyRef.current) return;
    lastIdentityKeyRef.current = identityKey;

    analytics.identify(userId, traits);
  }, [currentUser]);
};

const SENSITIVE_KEY_PATTERNS = [
  /token/i,
  /secret/i,
  /password/i,
  /apiKey/i,
  /api_key/i,
];

const isSensitiveKey = (key) =>
  SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key));

const buildTraits = ({ firebaseUser, userDoc }) => {
  const traits = {};

  if (firebaseUser) {
    if (firebaseUser.email) traits.email = firebaseUser.email;
    if (firebaseUser.displayName) traits.name = firebaseUser.displayName;
    if (firebaseUser.emailVerified !== undefined) {
      traits.emailVerified = firebaseUser.emailVerified;
    }
  }

  if (userDoc && typeof userDoc === "object") {
    for (const [key, value] of Object.entries(userDoc)) {
      if (isSensitiveKey(key)) continue;
      // Skip functions, Firestore Timestamps, and large nested objects.
      if (value === null || value === undefined) continue;
      const type = typeof value;
      if (type === "function") continue;
      if (type === "object" && !Array.isArray(value)) {
        // Firestore Timestamps have toDate() — surface as ISO string.
        if (typeof value.toDate === "function") {
          try {
            traits[key] = value.toDate().toISOString();
          } catch (_error) {
            /* skip */
          }
          continue;
        }
        // Otherwise skip nested docs to keep person properties flat.
        continue;
      }
      traits[key] = value;
    }
  }

  return traits;
};
